import React, { useState, useEffect, useRef } from "react";
import { MapPin, MessageCircleOff, PhoneOff } from "lucide-react";

/**
 * Modular Countdown Overlay component for TV displays.
 * Shows countdown, adhan, iqama, jummah silence, and forbidden prayer time announcements.
 *
 * @param {Object} props
 * @param {string} props.overlayMode - Current mode: null | "countdown" | "adhan" | "iqama" | "jummah" | "forbidden"
 * @param {string} props.overlayPrayerName - Name of the prayer being announced
 * @param {number} props.overlayCountdownSeconds - Seconds remaining in countdown
 * @param {string} props.overlayType - Type of overlay: "adhan" | "iqama" | "jummah" | "forbidden"
 * @param {boolean} props.screenDimmed - Whether screen should be dimmed (iqama/jummah mode)
 * @param {boolean} props.iqamaExtraDimmed - Extra dimming after 1 min of iqama
 * @param {Object} props.masjidData - Masjid info (name, city, state)
 * @param {string} props.primaryColor - Theme primary color
 * @param {boolean} props.isDark - Whether dark mode is active
 * @param {Object} props.simplifiedPrayerTimes - Simplified prayer times for forbidden screen (optional)
 */
// Valid overlay modes - only render if mode matches one of these
const VALID_OVERLAY_MODES = ["countdown", "adhan", "iqama", "jummah", "forbidden"];

export function CountdownOverlay({
  overlayMode,
  overlayPrayerName,
  overlayCountdownSeconds,
  overlayType,
  screenDimmed,
  iqamaExtraDimmed,
  masjidData,
  primaryColor,
  isDark,
  simplifiedPrayerTimes,
}) {
  // Exit animation state management to prevent black box artifact on Android TV browsers
  const [isExiting, setIsExiting] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const prevOverlayModeRef = useRef(overlayMode);

  // Track overlay mode changes for exit animation
  useEffect(() => {
    const wasActive = VALID_OVERLAY_MODES.includes(prevOverlayModeRef.current);
    const isActive = VALID_OVERLAY_MODES.includes(overlayMode);

    if (isActive && !shouldRender) {
      // Entering: show immediately
      setShouldRender(true);
      setIsExiting(false);
    } else if (!isActive && wasActive && shouldRender) {
      // Exiting: start fade-out, then unmount after transition completes
      setIsExiting(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsExiting(false);
      }, 500); // Match transition duration
      return () => clearTimeout(timer);
    }

    prevOverlayModeRef.current = overlayMode;
  }, [overlayMode, shouldRender]);

  // Only render if shouldRender is true (handles exit animation properly)
  if (!shouldRender) return null;

  // Reusable prayer times bar component - always dark mode
  const renderPrayerTimesBar = () => {
    if (!simplifiedPrayerTimes) return null;

    const barBgColor = "rgba(255,255,255,0.03)";
    const borderColor = "rgba(255,255,255,0.15)";
    const labelColor = "rgba(255,255,255,0.6)";
    const timeColor = "#ffffff";

    return (
      <div
        className="w-full pb-[2vh]"
        style={{ backgroundColor: barBgColor, boxSizing: "border-box", borderTop: `2px solid ${borderColor}` }}
      >
        <div
          className="flex justify-around py-[2.5vh] px-[2vw]"
        >
          {["Fajr", "Sunrise", "Dhuhr", "Asr", "Maghrib", "Isha"].map((prayer) => {
            const prayerData = simplifiedPrayerTimes[prayer.toLowerCase()];
            const adhanTime = typeof prayerData === 'object' ? prayerData?.adhan : prayerData;
            const iqamaTime = typeof prayerData === 'object' ? prayerData?.iqama : null;
            const isSunrise = prayer.toLowerCase() === 'sunrise';

            return (
              <div key={prayer} className="flex flex-col items-center">
                <span
                  style={{
                    fontSize: "2.5vh",
                    color: labelColor,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    fontWeight: 500,
                  }}
                >
                  {prayer}
                </span>
                <span
                  className="font-bold"
                  style={{
                    fontSize: "3.5vh",
                    color: timeColor,
                  }}
                >
                  {adhanTime || "-"}
                </span>
                {!isSunrise && iqamaTime && (
                  <span
                    className="font-semibold"
                    style={{
                      fontSize: "2.8vh",
                      color: primaryColor,
                    }}
                  >
                    {iqamaTime}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Header - always dark mode
  const renderHeader = () => (
    <div className="absolute top-0 left-0 right-0 h-[10vh] px-[3vw] flex items-center justify-between">
      <div className="shrink-0">
        <h1
          className="font-bold tracking-tight text-[3.5vh] leading-tight"
          style={{
            color: overlayMode === "iqama" ? "rgba(255,255,255,0.9)" : "#ffffff",
          }}
        >
          {masjidData?.name || "Masjid"}
        </h1>
        <div
          className="flex items-center gap-[0.8vh] mt-[0.2vh] text-[2vh]"
          style={{
            color: overlayMode === "iqama" ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.6)",
          }}
        >
          <MapPin className="h-[2vh] w-[2vh]" />
          <span>
            {masjidData?.city || "City"}, {masjidData?.state || "State"}
          </span>
        </div>
      </div>
      <div className="shrink-0 flex items-center justify-center pr-[1vw]">
        <span
          className="text-[5vh] font-bold"
          style={{
            color: primaryColor,
          }}
        >
          Muslim+
        </span>
      </div>
    </div>
  );

  return (
    <div
      className="absolute inset-0 z-[99998] flex flex-col items-center justify-center transition-opacity duration-500"
      style={{
        backgroundColor: screenDimmed ? "rgb(0,0,0)" : "rgb(10,10,10)",
        // No backdrop-filter: the background is opaque so a backdrop blur is invisible anyway,
        // and backdrop-filter is the root cause of the black-box artifact on the Android TV
        // box's WebView (the blur layer fails to composite and paints as a solid black rect).
        opacity: isExiting ? 0 : 1, // Fade out during exit animation
        transform: "translateZ(0)", // Force GPU layer for proper cleanup on Android TV browsers
      }}
    >
      {/* Countdown Mode - 1 minute before */}
      {overlayMode === "countdown" && (
        <div className="flex flex-col items-center justify-center text-center h-full w-full relative overflow-hidden">
          {/* Animated gradient wave background */}
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 180% 100% at 50% 110%, ${primaryColor} 0%, ${primaryColor}80 20%, transparent 55%)`,
              animation: "pulseWave 4s ease-in-out infinite",
            }}
          />
          <style>{`
            @keyframes pulseWave {
              0%, 100% { opacity: 0.35; transform: scale(1) translateY(0); }
              50% { opacity: 0.55; transform: scale(1.08) translateY(-3%); }
            }
          `}</style>

          {renderHeader()}

          {/* Main content - flex-1 to push prayer times to bottom */}
          <div className="flex-1 flex flex-col items-center justify-center relative z-10 w-full">
            {/* Large seconds countdown */}
            <div
              className="font-sans font-bold tabular-nums leading-none"
              style={{
                fontSize: "45vh",
                color: primaryColor,
              }}
            >
              {overlayCountdownSeconds}
            </div>

            {/* Label */}
            <div
              className="uppercase tracking-[0.3em] font-medium mt-[2vh]"
              style={{
                fontSize: "6vh",
                color: "rgba(255,255,255,0.9)",
              }}
            >
              Seconds to
            </div>
            <div
              className="uppercase tracking-[0.3em] font-bold mt-[1vh]"
              style={{
                fontSize: "10vh",
                color: primaryColor,
                textShadow: `0 0 80px ${primaryColor}80`,
              }}
            >
              {overlayPrayerName} {overlayType === "adhan" ? "Adhan" : "Iqamah"}
            </div>
          </div>

          {/* Prayer times bar at bottom */}
          <div className="relative z-10 w-full">
            {renderPrayerTimesBar()}
          </div>
        </div>
      )}

      {/* Adhan Announcement Mode */}
      {overlayMode === "adhan" && (
        <div className="flex flex-col items-center text-center h-full w-full">
          {renderHeader()}

          {/* Main content - flex-1 to push prayer times to bottom */}
          <div className="flex-1 flex flex-col items-center justify-center">
            {/* Prayer Name */}
            <div
              className="font-bold uppercase tracking-wider"
              style={{
                fontSize: "18vh",
                color: primaryColor,
              }}
            >
              {overlayPrayerName}
            </div>
            <div
              className="uppercase tracking-[0.4em] font-semibold mt-[3vh]"
              style={{
                fontSize: "10vh",
                color: "rgba(255,255,255,0.95)",
              }}
            >
              Adhan
            </div>
          </div>

          {/* Prayer times bar at bottom */}
          {renderPrayerTimesBar()}
        </div>
      )}

      {/* Iqama Announcement Mode - with message and dimmed screen */}
      {overlayMode === "iqama" && (
        <div
          className="flex flex-col items-center text-center h-full w-full transition-opacity duration-1000"
          style={{ opacity: iqamaExtraDimmed ? 0.15 : 1 }}
        >
          {renderHeader()}

          {/* Main content - flex-1 to push prayer times to bottom */}
          <div className="flex-1 flex flex-col items-center justify-center px-[4vw]">
            {/* Icons */}
            <div className="flex items-center gap-[4vw] mb-[4vh]">
              <div
                className="rounded-full p-[2vh] border-2"
                style={{
                  borderColor: "rgba(239, 68, 68, 0.6)",
                  backgroundColor: "rgba(239, 68, 68, 0.15)",
                }}
              >
                <MessageCircleOff
                  style={{
                    width: "8vh",
                    height: "8vh",
                    color: "rgba(239, 68, 68, 0.9)",
                  }}
                />
              </div>
              <div
                className="rounded-full p-[2vh] border-2"
                style={{
                  borderColor: "rgba(239, 68, 68, 0.6)",
                  backgroundColor: "rgba(239, 68, 68, 0.15)",
                }}
              >
                <PhoneOff
                  style={{
                    width: "8vh",
                    height: "8vh",
                    color: "rgba(239, 68, 68, 0.9)",
                  }}
                />
              </div>
            </div>

            {/* Time for Prayer */}
            <div
              className="uppercase tracking-[0.2em] font-medium"
              style={{
                fontSize: "6vh",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              Time for
            </div>
            <div
              className="font-bold uppercase tracking-wider mt-[2vh]"
              style={{
                fontSize: "16vh",
                color: primaryColor,
                textShadow: `0 0 80px ${primaryColor}80`,
              }}
            >
              {overlayPrayerName} Prayer
            </div>

            {/* Message */}
            <div
              className="mt-[6vh] max-w-[80vw]"
              style={{
                fontSize: "5vh",
                color: "rgba(255,255,255,0.85)",
                lineHeight: 1.4,
              }}
            >
              Straighten the lines and fill in the gaps
            </div>
            <div
              className="mt-[2vh]"
              style={{
                fontSize: "4vh",
                color: "rgba(255,255,255,0.6)",
              }}
            >
              Please silence your devices
            </div>
          </div>
        </div>
      )}

      {/* Jummah Silence Mode - similar to iqama but for Jummah khutbah */}
      {overlayMode === "jummah" && (
        <div
          className="flex flex-col items-center text-center h-full w-full transition-opacity duration-1000"
          style={{ opacity: iqamaExtraDimmed ? 0.15 : 1 }}
        >
          {renderHeader()}

          {/* Main content - flex-1 to push prayer times to bottom */}
          <div className="flex-1 flex flex-col items-center justify-center px-[4vw]">
            {/* Icons */}
            <div className="flex items-center gap-[4vw] mb-[4vh]">
              <div
                className="rounded-full p-[2vh] border-2"
                style={{
                  borderColor: "rgba(239, 68, 68, 0.6)",
                  backgroundColor: "rgba(239, 68, 68, 0.15)",
                }}
              >
                <MessageCircleOff
                  style={{
                    width: "8vh",
                    height: "8vh",
                    color: "rgba(239, 68, 68, 0.9)",
                  }}
                />
              </div>
              <div
                className="rounded-full p-[2vh] border-2"
                style={{
                  borderColor: "rgba(239, 68, 68, 0.6)",
                  backgroundColor: "rgba(239, 68, 68, 0.15)",
                }}
              >
                <PhoneOff
                  style={{
                    width: "8vh",
                    height: "8vh",
                    color: "rgba(239, 68, 68, 0.9)",
                  }}
                />
              </div>
            </div>

            {/* Jummah Khutbah */}
            <div
              className="uppercase tracking-[0.2em] font-medium"
              style={{
                fontSize: "6vh",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              Jummah
            </div>
            <div
              className="font-bold uppercase tracking-wider mt-[2vh]"
              style={{
                fontSize: "16vh",
                color: primaryColor,
                textShadow: `0 0 80px ${primaryColor}80`,
              }}
            >
              Khutbah
            </div>

            {/* Message */}
            <div
              className="mt-[6vh] max-w-[80vw]"
              style={{
                fontSize: "5vh",
                color: "rgba(255,255,255,0.85)",
                lineHeight: 1.4,
              }}
            >
              Please listen attentively
            </div>
            <div
              className="mt-[2vh]"
              style={{
                fontSize: "4vh",
                color: "rgba(255,255,255,0.6)",
              }}
            >
              Silence your devices
            </div>
          </div>

          {/* Prayer times bar at bottom */}
          {renderPrayerTimesBar()}
        </div>
      )}

      {/* Forbidden Prayer Time Mode - after sunrise */}
      {overlayMode === "forbidden" && (
        <div className="flex flex-col items-center text-center h-full w-full relative overflow-hidden">
          {/* Animated gradient wave background - same as countdown mode */}
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 180% 100% at 50% 110%, ${primaryColor} 0%, ${primaryColor}80 20%, transparent 55%)`,
              animation: "pulseWaveForbidden 4s ease-in-out infinite",
            }}
          />
          <style>{`
            @keyframes pulseWaveForbidden {
              0%, 100% { opacity: 0.35; transform: scale(1) translateY(0); }
              50% { opacity: 0.55; transform: scale(1.08) translateY(-3%); }
            }
          `}</style>

          {renderHeader()}

          {/* Main content - flex-1 to push prayer times to bottom */}
          <div className="flex-1 flex flex-col items-center relative z-10 w-full pt-[11vh]">
            {/* Current time - above title */}
            <div
              className="font-bold tabular-nums"
              style={{
                fontSize: "8vh",
                color: primaryColor,
                textShadow: `0 0 60px ${primaryColor}50`,
              }}
            >
              {new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
            </div>

            {/* Title */}
            <div
              className="uppercase tracking-[0.2em] font-bold"
              style={{
                fontSize: "10vh",
                color: "#ffffff",
                textShadow: "0 0 40px rgba(255,255,255,0.3)",
              }}
            >
              Forbidden to Pray
            </div>
            <div
              className="uppercase tracking-[0.15em]"
              style={{
                fontSize: "3.5vh",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              After Sunrise
            </div>

            {/* Large countdown display - under the title */}
            <div
              className="font-sans font-bold tabular-nums leading-none"
              style={{
                fontSize: "28vh",
                color: primaryColor,
                textShadow: `0 0 100px ${primaryColor}60`,
              }}
            >
              {Math.floor(overlayCountdownSeconds / 60)}:{String(overlayCountdownSeconds % 60).padStart(2, "0")}
            </div>

            {/* Label */}
            <div
              className="uppercase tracking-[0.3em] font-medium"
              style={{
                fontSize: "4vh",
                color: "rgba(255,255,255,0.9)",
              }}
            >
              Minutes Remaining
            </div>
          </div>

          {/* Prayer times bar at bottom */}
          <div className="relative z-10 w-full">
            {renderPrayerTimesBar()}
          </div>
        </div>
      )}
    </div>
  );
}

export default CountdownOverlay;
